import { Component, OnInit, OnDestroy, Input, ElementRef, ViewChild, NgZone, AfterViewInit } from '@angular/core';

@Component({
    selector: 'sentinel-scroll-down',
    templateUrl: './sentinel-scroll-down.component.html',
    styleUrls: ['./sentinel-scroll-down.component.scss'],
    standalone: false
})
export class SentinelScrollDownComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() whenToShow: () => boolean;
  @Input() enableScrollBoxSentinel: boolean = true;
  @Input() scrollContainerSelector: string | HTMLElement |null = null; // CSS selector for the scrollable container
  @ViewChild('scrollSentinel', { static: true }) scrollSentinel: ElementRef;

  showScrollDownBox = true;
  private intersectionObserver: IntersectionObserver;

  constructor(private zone: NgZone, private hostElement: ElementRef) {}

  ngOnInit() {}

  ngAfterViewInit() {
    if (this.enableScrollBoxSentinel) {
      this.setupIntersectionObserver();
    }
  }

  ngOnDestroy() {
    if (this.enableScrollBoxSentinel) {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
    }
  }

  setupIntersectionObserver() {
    // const scrollContainer = this.getScrollContainer();
    let rootValue = this.getScrollContainer();
    
    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.zone.run(() => {
              this.showScrollDownBox = !entry.isIntersecting;
              if (this.whenToShow) {
                this.showScrollDownBox = this.showScrollDownBox && this.whenToShow();
              }
            });
          });
        },
        {
          root: rootValue,
          threshold: 0.1,
          rootMargin: '0px 0px 0px 0px',
        }
      );

      if (this.scrollSentinel && this.scrollSentinel.nativeElement) {
        this.intersectionObserver.observe(this.scrollSentinel.nativeElement);
      }
    } else {
      this.showScrollDownBox = false;
    }
  }

  getRootValue() {
    let rootValue = null;

    if (typeof this.scrollContainerSelector === 'string') {
      rootValue = document.querySelector(this.scrollContainerSelector);
    }
    if (this.scrollContainerSelector instanceof HTMLElement) {
      rootValue = this.scrollContainerSelector;
    }
    
    return rootValue;
  }

  getScrollContainer(): HTMLElement | null {
    if (this.scrollContainerSelector === null
      || this.scrollContainerSelector === 'window'
      || this.scrollContainerSelector === 'document'
      || this.scrollContainerSelector === 'body'
      || this.scrollContainerSelector === 'html'
      || this.scrollContainerSelector === 'viewport' 
    ) {
      return null;
    }

    if (this.scrollContainerSelector && this.scrollContainerSelector !== 'window') {

      const scrollableEl = this.getRootValue();
      return this.getScrollableParent(scrollableEl);

    } else {
      // Default to the first scrollable parent
      let parent = this.hostElement.nativeElement.parentElement;
      while (parent) {
        const overflowY = window.getComputedStyle(parent).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    }
  }

  getScrollableParent(node: any): HTMLElement | null {
    if (!node) {
      return null;
    }
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }
    return this.getScrollableParent(node.parentElement);
  }

  getScrollableChildren(node: any): HTMLElement[] {
    if (!node) {
      return [];
    }
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return [node];
    }
    return Array.from(node.children).reduce<HTMLElement[]>((acc, child) => {
      return acc.concat(this.getScrollableChildren(child));
    }, []);
  }

  tryNodeChildNumber(children: HTMLElement[], num: number): HTMLElement | null {
    for (let i = 0; i < children.length; i++) {
      if (i === num) {
        return children[i];
      }
    }
    return null;
  }
}